import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import Alert from '../../components/ui/Alert'
import Footer from '../../components/layout/Footer'
import Wordmark from '../../components/layout/Wordmark'

// Signup público self-service: cria empresa nova + trial de 14 dias
// (via trigger handle_new_user). Convite por admin continua existindo.
type Tab = 'login' | 'signup'

function EyeIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  )
}

function EyeOffIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  )
}

const inputCls = 'w-full border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-600/20 focus:border-brand-600 transition-colors'

export default function LoginPage() {
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  function resetForm() {
    setEmail('')
    setPassword('')
    setConfirmPassword('')
    setError(null)
    setSuccessMsg(null)
    setShowPassword(false)
  }

  function switchTab(t: Tab) {
    setTab(t)
    resetForm()
  }

  async function handleLogin(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) setError('E-mail ou senha incorretos.')
    else navigate('/')
    setLoading(false)
  }

  async function handleSignup(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (password !== confirmPassword) { setError('As senhas não coincidem.'); return }
    if (password.length < 6) { setError('A senha deve ter pelo menos 6 caracteres.'); return }
    setLoading(true)
    const { error } = await supabase.auth.signUp({ email, password, options: { emailRedirectTo: 'https://bx.tec.br' } })
    if (error) setError(error.message)
    else { setSuccessMsg('Conta criada! Verifique seu e-mail para confirmar o cadastro.'); resetForm() }
    setLoading(false)
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex">
      {/* ── Painel esquerdo — formulário ── */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-paper p-8 lg:p-16">
        <div className="w-full max-w-sm">
          <Wordmark iconClassName="h-11 w-11" textClassName="text-2xl" className="mb-8" />

          {tab === 'login' ? (
            <>
              <p className="text-ink-muted text-sm mb-1">Olá, 👋</p>
              <h1 className="text-2xl font-bold text-ink mb-8">Bem-vindo de volta</h1>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-ink-faint uppercase tracking-wide mb-1.5">E-mail</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    required
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-ink-faint uppercase tracking-wide mb-1.5">Senha</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className={`${inputCls} pr-10`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink-muted transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                </div>

                {error && <Alert type="error" message={error} />}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg text-sm transition-colors"
                >
                  {loading ? 'Entrando...' : 'Entrar'}
                </button>
              </form>

              <p className="text-center text-xs text-ink-muted mt-6">
                Não tem uma conta?{' '}
                <button
                  type="button"
                  onClick={() => switchTab('signup')}
                  className="text-brand-600 hover:text-brand-700 font-semibold transition-colors"
                >
                  Criar conta grátis
                </button>
              </p>
            </>
          ) : (
            <>
              <button
                onClick={() => switchTab('login')}
                className="flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink-muted mb-6 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Voltar
              </button>

              <h1 className="text-2xl font-bold text-ink mb-2">Criar nova conta</h1>
              <p className="text-ink-muted text-sm mb-8">Comece com 14 dias grátis, sem cartão.</p>

              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-ink-faint uppercase tracking-wide mb-1.5">E-mail</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    required
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-ink-faint uppercase tracking-wide mb-1.5">Senha</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      required
                      className={`${inputCls} pr-10`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink-muted transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-ink-faint uppercase tracking-wide mb-1.5">Confirmar senha</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className={inputCls}
                  />
                </div>

                {error && <Alert type="error" message={error} />}
                {successMsg && <Alert type="success" message={successMsg} />}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg text-sm transition-colors"
                >
                  {loading ? 'Criando conta...' : 'Criar conta'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* ── Painel direito — branding ── */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0D1B2A] flex-col items-center justify-center p-16 text-white relative overflow-hidden">
        <div
          className="absolute -top-24 -right-16 w-96 h-96 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(255,138,0,0.22), transparent 65%)' }}
        />
        <div className="relative flex flex-col items-center">
          <Wordmark iconClassName="h-16 w-16" textClassName="text-4xl" onDark className="mb-8" />
          <p className="text-center text-3xl font-semibold leading-snug max-w-md">
            Gestão inteligente.<br /><span className="text-brand-500">Resultados reais.</span>
          </p>
          <p className="text-center text-white/60 text-sm mt-5 max-w-sm leading-relaxed">
            Controle seu estoque e compras com método kanban, em tempo real.
          </p>
        </div>
      </div>
      </div>
      <Footer variant="light" />
    </div>
  )
}
